import axios from "axios";
import { PUBLIC_SEGMENTS } from "../constants";

// Setup axios response interceptor to handle 401 errors
const isPublicRoute = (path) =>
  PUBLIC_SEGMENTS.some((segment) => path.includes(segment));

export const setupAxiosInterceptor = () => {
  // Response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Check if response contains "Invalid token" error even on 200 status
      if (response.data?.error === "Invalid token") {
        // Get current path
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search || "";

        // Check if we're already on login page or a public route (no redirect needed)
        const isLoginPage =
          currentPath === "/ri-doc-front/login" ||
          currentPath === "/login" ||
          currentPath.endsWith("/login") ||
          currentPath.includes("/login");

        if (!isLoginPage && !isPublicRoute(currentPath)) {
          // Extract basename from current path (e.g., /dev, /ri-doc-front, etc.)
          // Split path and get the first segment after root
          const pathSegments = currentPath.split("/").filter(Boolean);
          const basename = pathSegments.length > 0 ? `/${pathSegments[0]}/` : "/";
          
          // Store the current location so we can redirect back after login
          // Store the full path including basename for proper redirect
          const fullPath = currentPath + currentSearch;

          // Only store if not already stored (to preserve original path)
          if (!sessionStorage.getItem("redirectAfterLogin")) {
            // Store the path relative to basename (remove basename prefix)
            let pathToStore = currentPath;
            if (basename && pathToStore.startsWith(basename)) {
              pathToStore = pathToStore.replace(basename, "") || "/";
            }
            // Ensure path starts with /
            if (!pathToStore.startsWith("/")) {
              pathToStore = "/" + pathToStore;
            }
            sessionStorage.setItem("redirectAfterLogin", pathToStore + currentSearch);
            // Also store basename for login redirect
            sessionStorage.setItem("redirectBasename", basename);
          }

          // Clear auth tokens
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          localStorage.removeItem("user_id");
          localStorage.removeItem("user_name");
          localStorage.removeItem("loggedinUserInfo");
          // Note: Don't clear rememberMe here as it's a user preference

          // Clear axios auth header
          delete axios.defaults.headers.common["Authorization"];

          // Determine login path using detected basename
          const loginPath = basename === "/" ? "/login" : `${basename}login`;

          // Redirect to login page immediately
          // Use window.location.replace to prevent back button issues
          window.location.replace(loginPath);
        }

        // Return a rejected promise to trigger error handling
        return Promise.reject({
          response: {
            data: { error: "Invalid token" },
            status: 401,
          },
        });
      }

      // If the request succeeds, just return the response
      return response;
    },
    (error) => {
      // Handle 401 Unauthorized errors
      if (error.response && error.response.status === 401) {
        // Skip redirect for business logic errors that use 401 status code
        // These should be handled by the component, not treated as authentication errors
        const isPasswordError =
          error.response?.data?.message === "Incorrect password" ||
          error.response?.data?.error === "Incorrect password";

        const isEmailExistsError =
          error.response?.data?.message === "User Email already exists" ||
          error.response?.data?.error === "User Email already exists";

        if (isPasswordError || isEmailExistsError) {
          // Return the error so it can be handled by the calling code (e.g., show snackbar)
          return Promise.reject(error);
        }

        // Get current path
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search || "";

        // Check if we're already on login page or a public route (no redirect needed)
        const isLoginPage =
          currentPath === "/ri-doc-front/login" ||
          currentPath === "/login" ||
          currentPath.endsWith("/login") ||
          currentPath.includes("/login");

        if (!isLoginPage && !isPublicRoute(currentPath)) {
          // Extract basename from current path (e.g., /dev, /ri-doc-front, etc.)
          // Split path and get the first segment after root
          const pathSegments = currentPath.split("/").filter(Boolean);
          const basename = pathSegments.length > 0 ? `/${pathSegments[0]}/` : "/";
          
          // Store the current location so we can redirect back after login
          // Store the path relative to basename
          let pathToStore = currentPath;
          if (basename && pathToStore.startsWith(basename)) {
            pathToStore = pathToStore.replace(basename, "") || "/";
          }
          // Ensure path starts with /
          if (!pathToStore.startsWith("/")) {
            pathToStore = "/" + pathToStore;
          }
          const fullPath = pathToStore + currentSearch;

          // Only store if not already stored (to preserve original path)
          if (!sessionStorage.getItem("redirectAfterLogin")) {
            sessionStorage.setItem("redirectAfterLogin", fullPath);
            // Also store basename for login redirect
            sessionStorage.setItem("redirectBasename", basename);
          }

          // Clear auth tokens
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          localStorage.removeItem("user_id");
          localStorage.removeItem("user_name");
          localStorage.removeItem("loggedinUserInfo");
          // Note: Don't clear rememberMe here as it's a user preference

          // Clear axios auth header
          delete axios.defaults.headers.common["Authorization"];

          // Determine login path using detected basename
          const loginPath = basename === "/" ? "/login" : `${basename}login`;

          // Redirect to login page immediately
          // Use window.location.replace to prevent back button issues
          window.location.replace(loginPath);

          // Return a resolved promise to prevent further error handling
          return Promise.resolve();
        }
      }

      // Return the error so it can be handled by the calling code
      return Promise.reject(error);
    }
  );
};
